import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../api/auth/useAuth";
import {
  isAnnualPricingPlanDuration,
  isFreePricingPlan,
  isStoreBilledSubscription,
} from "../api/services/pricing-plan.billing";
import { usePaymentInfo } from "../api/services/pricing-plan.hooks";
import {
  ANNUAL_DISCOUNT_PROMO_ENABLED,
  ANNUAL_DISCOUNT_PROMO_PERCENT,
  dismissAnnualDiscountModalForCurrentLogin,
  wasAnnualDiscountModalDismissedForCurrentLogin,
} from "../config/annualDiscountPromo";
import { Paths } from "../router/paths";
import { splitLocalePath } from "../i18n/localePath";
import { useShouldHideSubscriptions } from "./useShouldHideSubscriptions";

const EMBEDDED_APP_PATHS: readonly string[] = [
  Paths.AKINDA_AI,
  Paths.GAMES,
  // Native page now, but kept promo-free as when it was an embedded app.
  Paths.BACKTESTING,
];

function isEmbeddedPath(pathname: string): boolean {
  return EMBEDDED_APP_PATHS.includes(pathname);
}

function shouldSkipModal(pathname: string): boolean {
  if (pathname === Paths.SUBSCRIPTIONS) return true;
  if (pathname === Paths.SUBSCRIPTION_RESULT) return true;
  if (pathname === Paths.PERSONAL_INFO) return true;
  if (pathname.startsWith("/payment-details")) return true;
  if (pathname.startsWith("/subscribe/")) return true;
  return false;
}

export function useAnnualDiscountPromo() {
  const { token } = useAuth();
  const { pathname: urlPathname } = useLocation();
  const pathname = splitLocalePath(urlPathname).pathname;
  const hideSubscriptions = useShouldHideSubscriptions();
  const paymentQueryEnabled = ANNUAL_DISCOUNT_PROMO_ENABLED && Boolean(token);
  const { data: paymentInfo, isLoading, isError } = usePaymentInfo({
    enabled: paymentQueryEnabled,
  });

  const [modalDismissed, setModalDismissed] = useState(() =>
    wasAnnualDiscountModalDismissedForCurrentLogin(),
  );

  useEffect(() => {
    setModalDismissed(wasAnnualDiscountModalDismissedForCurrentLogin());
  }, [token]);

  const isEligible = useMemo(() => {
    if (!ANNUAL_DISCOUNT_PROMO_ENABLED) return false;
    if (hideSubscriptions) return false;

    if (!token) return true;

    if (isLoading && !paymentInfo) return false;
    if (isError || !paymentInfo) return false;
    if (isStoreBilledSubscription(paymentInfo)) return false;

    const current = paymentInfo.current_plan;
    // Hide only while yearly auto-renew is on. Canceled yearly users keep
    // current_plan until period end and must still see the campaign.
    if (
      current &&
      !isFreePricingPlan(current) &&
      isAnnualPricingPlanDuration(current.duration) &&
      paymentInfo.auto_renew
    ) {
      return false;
    }

    return true;
  }, [hideSubscriptions, isError, isLoading, paymentInfo, token]);

  const isEmbedded = isEmbeddedPath(pathname);

  const showBar = isEligible && !isEmbedded;
  const showModal =
    isEligible &&
    !modalDismissed &&
    !isEmbedded &&
    !shouldSkipModal(pathname);

  const dismissModal = useCallback(() => {
    dismissAnnualDiscountModalForCurrentLogin();
    setModalDismissed(true);
  }, []);

  return {
    enabled: ANNUAL_DISCOUNT_PROMO_ENABLED,
    percent: ANNUAL_DISCOUNT_PROMO_PERCENT,
    isEligible,
    showBar,
    showModal,
    dismissModal,
  };
}
